import { manualePubblico, type ManualePubblico } from "@/lib/manualiPubblici";
import type { SchemaRiferimento } from "@/lib/schemaImage";
import mappaGenerata from "@/lib/schemiManuale.json";

/**
 * Da quale manuale viene ogni schema di kg_SCHEMI.
 *
 * Davide, 02/09/2026: «bisogna far vedere lo schema, è importante, ma se
 * clicco mi deve portare alla pagina del costruttore, senza uscire dall'app».
 * L'anteprima è la nostra copia (il nostro archivio), il click è del
 * costruttore: qui sta il ponte fra il nome dello schema (SCHEMA__<modello>__pNNN)
 * e il manuale da cui la pagina è stata presa. Per i costruttori che
 * pubblicano i manuali (Notifier, Ajax) il click va da loro; per gli altri
 * (INIM, Daitem: serve il permesso scritto) resta il nostro visore.
 *
 * Le chiavi sono il "modello" così com'è nel nome del documento, con gli
 * underscore.
 *
 * 06/09/2026 (secondo giro di Astra 6, A5): qui c'erano 26 righe, e 25 dicevano
 * la stessa cosa della mappa generata (`schemiManuale.json`, confrontate una
 * per una). Una diceva il FALSO: Superior_Transmitter_Fibra puntava al manuale
 * del MultiTransmitter (era vero il 31/08, quando le pagine si prendevano da
 * li'; dal 04/09 il Transmitter ha il suo manuale, e la mappa generata lo sa)
 * e, vincendo sulla mappa, mandava l'installatore al manuale di un altro
 * dispositivo. Le righe uguali sono state tolte: una riga a mano che ripete
 * la mappa non aggiunge niente oggi e domani puo' contraddirla in silenzio.
 * Resta SOLO cio' che la mappa generata non ha.
 */
const SCRITTI_A_MANO: Record<string, string> = {
  // M701E non sta nella mappa generata (il suo schema viene da una scheda
  // tecnica, non da un manuale caricato con gli schemi).
  M701E: "NOTIFIER__M701E__Scheda-Tecnica__IT.pdf",
};

/**
 * L'elenco vero: quello GENERATO dal caricamento degli schemi, piu' le righe
 * scritte a mano, che vincono.
 *
 * Lo scrive `scripts/dify/mappa_schemi_manuale.py` leggendo le scelte del
 * caricamento (204 prodotti il 06/09/2026): un elenco a mano lungo cento
 * righe sarebbe gia' falso il giorno dopo, e uno schema fuori elenco esce
 * senza il © del costruttore. Le righe a mano vincono, quindi vanno tenute al
 * minimo: ogni riga in piu' e' una possibile contraddizione con la mappa.
 */
const MANUALE_DELLO_SCHEMA: Record<string, string> = {
  ...(mappaGenerata as Record<string, string>),
  ...SCRITTI_A_MANO,
};

export type FonteSchema = ManualePubblico & { href: string; manuale: string };

/** La pagina del costruttore per questo schema, se il costruttore la pubblica. */
export function fonteSchema(schema: SchemaRiferimento): FonteSchema | null {
  const chiave = schema.modello.replace(/ /g, "_");
  const manuale = MANUALE_DELLO_SCHEMA[chiave];
  if (!manuale) return null;
  const p = manualePubblico(manuale, schema.pagina);
  return p ? { ...p, manuale } : null;
}

/**
 * Di chi e' la pagina che stiamo mostrando.
 *
 * Serviva finche' lo schema viveva dentro l'elenco delle fonti, che il marchio
 * ce l'aveva gia'; dal 04/09 l'immagine sta dentro la risposta, e la
 * paternita' deve viaggiare con lei: e' la differenza fra citare e
 * appropriarsi (art. 70 l.d.a.). Se un modello non e' in elenco si restituisce
 * null e la didascalia non attribuisce niente a nessuno — meglio muti che
 * sbagliare padrone.
 */
const TITOLARE_PER_FAMIGLIA: [RegExp, string][] = [
  // AM-8200N, AM-LCD, ma anche AM2000 e AM4000 senza trattino: se il modello
  // non combacia, la didascalia resta senza il © del costruttore (04/09/2026)
  [/^(AM[-_ ]?\d|AM[-_ ]?LCD|AM[-_ ]?Serie|M701E)/i, "Notifier by Honeywell"],
  [/^Superior[_ ]/i, "Ajax Systems"],
  // niente \b: nei nomi dei file il modello è seguito da un underscore, che per
  // una regex è carattere di parola — "Previdia_Compact" non veniva riconosciuto
  // e la pagina di un manuale altrui restava senza padrone. PrimeX prima di Prime.
  [/^(Previdia|PrimeX|Prime|SmartLiving|SmartLine|SmartLevel|Sol)(?![A-Za-z])/i, "INIM Electronics"],
];

/** Il marchio scritto in testa al nome del manuale, che e' un dato e non un indizio. */
const TITOLARE_PER_MARCHIO: Record<string, string> = {
  NOTIFIER: "Notifier by Honeywell",
  AJAX: "Ajax Systems",
  INIM: "INIM Electronics",
  DAITEM: "Daitem — Hager Group",
};

export function titolareSchema(schema: SchemaRiferimento): string | undefined {
  const m = schema.modello.replace(/ /g, "_");
  // Prima il manuale da cui la pagina viene: dal 04/09 gli schemi portano il nome
  // del PRODOTTO (CMA22, P700, AW80US0…) e nessuna regola sulle sigle potrebbe
  // indovinarne il marchio. Il nome del file del manuale invece ce l'ha scritto.
  const manuale = MANUALE_DELLO_SCHEMA[m];
  if (manuale) {
    const marchio = manuale.split("__")[0].toUpperCase();
    if (TITOLARE_PER_MARCHIO[marchio]) return TITOLARE_PER_MARCHIO[marchio];
  }
  return TITOLARE_PER_FAMIGLIA.find(([re]) => re.test(m))?.[1];
}
